import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { computed, onBeforeMount, provide, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

import { crossbeamApi } from '@/api';
import { TEMP_PARTNER_FIELD_MAPPINGS } from '@/constants/feature_flags';
import { MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE } from '@/constants/integrations';
import { captureException } from '@/errors';
import {
  ProvidedIsTrayUpdateRequired,
  ProvidedTrayState,
} from '@/injectionKeys/integration_profiles';
import {
  useFeatureFlagStore,
  useFlashesStore,
  useIntegrationProfileStore,
  usePartnerCloudStore,
  usePartnersStore,
} from '@/stores';
import {
  EcosystemFieldMappings,
  EcosystemFieldsPush,
  PartnerOption,
  PutPartnerFieldMappingsPayload,
  SharedPartnerField,
  TrayPayload,
} from '@/types/integration_profiles';
import { Mapping, TrayExternalId, TrayTag } from '@/types/integrations';

export default function useTrayConfig() {
  const partnerCloudStore = usePartnerCloudStore();
  const route = useRoute();

  const { addErrorFlash } = useFlashesStore();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const pathTrayTag = computed(() => route.params.tray_tag as TrayTag);

  const enabledTray = computed(() => {
    const foundTray = partnerCloudStore.enabledTray.find(
      (trayIntegration) => trayIntegration?.trayTag === pathTrayTag.value,
    );
    return foundTray;
  });

  const isIntegrationEnabled = ref(enabledTray?.value?.enabled ?? false);

  function parseConfigValue<T = boolean | EcosystemFieldsPush>(
    externalId: TrayExternalId,
    defaultValue: T,
  ): T {
    const raw = enabledTray.value?.configValues?.find(
      (c) => c.externalId === externalId,
    )?.value;
    if (!raw) return defaultValue;
    try {
      const parsed = JSON.parse(raw);
      return parsed;
    } catch {
      return defaultValue;
    }
  }

  const integrationProfileType = computed(() => {
    if (
      pathTrayTag.value &&
      pathTrayTag.value in MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE
    ) {
      const value = MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE[pathTrayTag.value];
      if (!value) {
        throw Error('Integration profile type not found');
      }
      return value;
    }

    return undefined;
  });

  const EMPTY_MAPPING = {
    id: 'temporary',
    source_field_id: null,
    partner_organization_uuid: null,
    partner_source_field_id: null,
    empty: true,
  };

  const initialState = {
    isEnabled: enabledTray.value?.enabled ?? false,
    oppPush: parseConfigValue<boolean>('external_opps-enabled', false),
    customObjectPush: parseConfigValue<boolean>(
      'external_push_overlaps',
      false,
    ),
    partnerPush: parseConfigValue<boolean>(
      'external_push_partner_field_mappings',
      false,
    ),
    accountPush: parseConfigValue<EcosystemFieldsPush>(
      'external_push_ecosystem_fields',
      'NONE',
    ),
    ecosystemMaps: parseConfigValue<EcosystemFieldMappings>(
      'external_ecosystem_fields',
      null,
    ),
    profileId: parseConfigValue<string | undefined>(
      'external_crossbeam-profile-id',
      undefined,
    ),
    partnerFieldMappings: [EMPTY_MAPPING] as Mapping[],
    sharedPartnerFields: [] as PartnerOption[],
  };

  const state = reactive({
    ...structuredClone(initialState),
    saving: false,
    profileType: integrationProfileType.value,
    saveTrayConfig,
  });

  const newIsTrayConfigUpdateRequired = computed(() => {
    if (state.isEnabled !== initialState.isEnabled) return true;
    if (state.oppPush !== initialState.oppPush) return true;
    if (state.customObjectPush !== initialState.customObjectPush) return true;
    if (state.partnerPush !== initialState.partnerPush) return true;
    if (state.accountPush !== initialState.accountPush) return true;
    if (!isEqual(state.ecosystemMaps, initialState.ecosystemMaps)) {
      return (
        Array.isArray(state.ecosystemMaps) &&
        state.ecosystemMaps?.length === 2 &&
        state.ecosystemMaps?.every((m) => !!m.crm_field)
      );
    }
    return false;
  });

  provide(ProvidedTrayState, state);
  provide(ProvidedIsTrayUpdateRequired, newIsTrayConfigUpdateRequired);

  const integrationProfileId = computed(() => {
    if (!integrationProfileType.value) return null;

    return integrationProfileStore.getIntegrationProfileByType(
      integrationProfileType.value,
    )?.profile_id;
  });

  const integrationProfileStore = useIntegrationProfileStore();
  const createTrayConfigPayload = (): TrayPayload => {
    const profileId = integrationProfileId.value;

    if (!profileId) {
      throw new Error('Integration profile settings not found');
    }

    if (!enabledTray?.value) {
      throw new Error('Tray integration info not found');
    }

    const config: TrayPayload['config'] = {
      integration_profile_id: profileId,
      start_timestamp: DateTime.now().plus({ minutes: 15 }).toISO(),
      enabled: state.isEnabled,
      opps_enabled: state.oppPush,
      push_overlaps: state.customObjectPush,
      push_ecosystem_fields: state.accountPush,
    };

    if (integrationProfileType.value === 'salesforce' && state.ecosystemMaps) {
      config.ecosystem_fields = state.ecosystemMaps;
    }

    return {
      config,
      instance_name: enabledTray.value.name,
      instance_id: enabledTray.value.id,
    };
  };

  const updateTrayInstanceConfig = async (payload: TrayPayload) => {
    const { error } = await crossbeamApi.PUT('/v0.1/tray-integrations', {
      body: payload,
    });
    if (error) {
      captureException(error);
      addErrorFlash({
        message: 'Failed to save integration settings',
      });
    }
  };

  async function savePartnerFieldMappings() {
    if (!integrationProfileId.value) return;

    const payload = state.partnerFieldMappings
      .filter(
        (m) => m.source_field_id !== null && m.partner_source_field_id !== null,
      )
      .map((m) => ({
        source_field_id: m.source_field_id,
        partner_source_field_id: m.partner_source_field_id,
        partner_organization_uuid: m.partner_organization_uuid,
      })) as PutPartnerFieldMappingsPayload;

    await crossbeamApi.PUT(
      '/v0.1/integration-profiles/{profile_id}/partner-field-mappings',
      {
        body: payload,
        params: {
          path: {
            profile_id: integrationProfileId.value,
          },
        },
      },
    );
  }

  async function saveTrayConfig() {
    try {
      const payload = createTrayConfigPayload();
      await updateTrayInstanceConfig(payload);
      await partnerCloudStore.refreshPartnerCloudStore();
    } catch (err) {
      addErrorFlash({
        message: 'Failed to save tray configuration',
      });
    }
  }

  const partnersStore = usePartnersStore();

  const mapPartnersToFields = (field: SharedPartnerField) => {
    const { organization_uuid, nickname, source_field_id, column } = field;
    const partner = partnersStore.getPartnerOrgByUuid(organization_uuid);
    if (!partner) {
      return null;
    }
    return {
      ...partner,
      users: partner.users ?? [],
      tags: partner.tags ?? [],
      label: nickname ?? column,
      value: source_field_id,
    };
  };

  const hasPartnerFieldMappings = computed(() =>
    hasFeatureFlag(TEMP_PARTNER_FIELD_MAPPINGS),
  );

  const showObjectTabs = computed(
    () =>
      hasPartnerFieldMappings.value &&
      ['salesforce', 'hubspot'].includes(integrationProfileType.value ?? ''),
  );

  onBeforeMount(async () => {
    if (!showObjectTabs.value || !integrationProfileId.value) return;

    const [sharedResponse, mappingResponse] = (await Promise.all([
      crossbeamApi.GET(
        `/v0.1/integration-profiles/{profile_id}/shared-partner-fields`,
        {
          params: {
            path: {
              profile_id: integrationProfileId.value,
            },
          },
        },
      ),
      crossbeamApi.GET(
        `/v0.1/integration-profiles/{profile_id}/partner-field-mappings`,
        {
          params: {
            path: {
              profile_id: integrationProfileId.value,
            },
          },
        },
      ),
    ])) as [
      { data: { items: SharedPartnerField[] } },
      { data?: { items: Mapping[] } },
    ];

    if (sharedResponse.data) {
      const mappedSharedFields = sharedResponse.data.items
        .reduce((acc, curr) => {
          const mappedField = mapPartnersToFields(curr);
          if (mappedField) acc.push(mappedField);
          return acc;
        }, [] as PartnerOption[])
        .sort((a, b) => a.name.localeCompare(b.name));
      initialState.sharedPartnerFields = mappedSharedFields;
      state.sharedPartnerFields = mappedSharedFields;
    }

    if (mappingResponse.data) {
      initialState.partnerFieldMappings = mappingResponse.data.items;
      state.partnerFieldMappings = mappingResponse.data.items;
    }
  });

  return {
    integrationProfileType,
    isTrayConfigUpdateRequired: newIsTrayConfigUpdateRequired,
    enabledTray,
    isIntegrationEnabled,
    hasPartnerFieldMappings,
    showObjectTabs,
    saveTrayConfig,
    savePartnerFieldMappings,
    state,
  };
}
